import React, { useEffect, useState } from 'react'
import CustomCard from '../../../components/common/CustomCard/CustomCard'
// import RightArrow from '../../../components/common/icons/RightArrow'
// import LeftArrow from '../../../components/common/icons/LeftArrow'
import { Button } from '../../../components/common'
import { useTranningCalender } from '../api'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../../store/authStore'

// ✅ Updated to consider booking period for status determination
const getTrainingStatus = (
  startDateStr: string,
  endDateStr?: string,
  bookingStartDateStr?: string,
  bookingEndDateStr?: string
) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const startDate = new Date(startDateStr)
  startDate.setHours(0, 0, 0, 0)

  const endDate = endDateStr ? new Date(endDateStr) : startDate
  endDate.setHours(0, 0, 0, 0)

  // If booking period exists, check if today is within booking dates
  if (bookingStartDateStr && bookingEndDateStr) {
    const bookingStartDate = new Date(bookingStartDateStr)
    bookingStartDate.setHours(0, 0, 0, 0)

    const bookingEndDate = new Date(bookingEndDateStr)
    bookingEndDate.setHours(0, 0, 0, 0)

    // If today is within booking period → "Upcoming" (bookable)
    if (today >= bookingStartDate && today <= bookingEndDate) {
      return 'Upcoming'
    }

    // If today is after booking end date but before training start → "Current" (booking closed)
    if (today > bookingEndDate && today < startDate) {
      return 'Current'
    }
  }

  // Fallback to original logic if no booking dates
  if (today < startDate) return 'Upcoming'
  if (today >= startDate && today <= endDate) return 'Current'
  return 'Completed'
}

const TrainingCalendar: React.FC = () => {
  const { data, isLoading, isError } = useTranningCalender()
  const trainings = data?.data || []
  const [startIndex, setStartIndex] = useState(0)
  const navigate = useNavigate()
  const { language } = useAuthStore()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // ✅ Filter "Current" and "Upcoming" trainings
  const filteredTrainings = trainings.filter((training: any) => {
    const bookingEndDate = training?.booking_end_date
      ? new Date(training.booking_end_date)
      : null
    if (bookingEndDate) {
      bookingEndDate.setHours(23, 59, 59, 999)
      if (bookingEndDate < today) {
        return false
      }
    }
    const status = getTrainingStatus(
      training?.date,
      training?.end_date,
      training?.booking_start_date,
      training?.booking_end_date
    )
    return status === 'Current' || status === 'Upcoming'
  })

  // const handlePrev = () => {
  //   setStartIndex(
  //     (prev) => (prev - 1 + filteredTrainings.length) % filteredTrainings.length
  //   )
  // }

  const handleNext = () => {
    setStartIndex((prev) => (prev + 1) % filteredTrainings.length)
  }

  // ✅ Auto-slide if more than 4
  useEffect(() => {
    if (filteredTrainings.length > 4) {
      const interval = setInterval(() => {
        handleNext()
      }, 6000)
      return () => clearInterval(interval)
    }
  }, [filteredTrainings.length])

  // ✅ Show exactly 4 at a time (sliding window)
  const visibleTrainings =
    filteredTrainings.length > 4
      ? [
          filteredTrainings[startIndex],
          filteredTrainings[(startIndex + 1) % filteredTrainings.length],
          filteredTrainings[(startIndex + 2) % filteredTrainings.length],
          filteredTrainings[(startIndex + 3) % filteredTrainings.length],
        ].filter(Boolean)
      : filteredTrainings

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  if (isLoading) return <div className="text-center">Loading...</div>
  if (isError)
    return (
      <div className="text-center text-red-500">Failed to load trainings</div>
    )

  return (
    <div className="w-11/12 mx-auto py-10 relative">
      <h1 className="text-2xl font-bold mb-6">
        {language === 'lang_mal' ? 'പരിശീലനം' : 'Training'}
      </h1>

      <div className="relative flex items-center justify-center">
        {/* Left Arrow */}
        {/* {filteredTrainings.length > 3 && (
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 rounded-full p-3 hover:bg-gray-200 z-10"
          >
            <LeftArrow />
          </button>
        )} */}

        {/* Cards */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 justify-items-center transition-all duration-700 ease-in-out"
          style={{ overflow: 'hidden' }}
        >
          {visibleTrainings.map((training: any, index: number) => {
            const status = getTrainingStatus(
              training?.date,
              training?.end_date,
              training?.booking_start_date,
              training?.booking_end_date
            )
            const isBookable = status === 'Upcoming'

            return (
              <CustomCard
                key={training.id || index}
                status={status}
                mode={training?.location}
                title={training.name}
                date={`${formatDate(training.date)} - ${formatDate(
                  training.end_date
                )}`}
                buttonText={isBookable ? 'Apply Now' : 'Enrollment Closed'}
                buttonDisabled={!isBookable}
                imageUrl={training.image}
                onClick={() =>
                  isBookable &&
                  navigate(
                    `/departments/learning/program-details/${training.id}`
                  )
                }
              />
            )
          })}
        </div>

        {/* Right Arrow */}
        {/* {filteredTrainings.length > 3 && (
          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 rounded-full p-3 hover:bg-gray-200 z-10"
          >
            <RightArrow />
          </button>
        )} */}
      </div>

      {/* View More Button */}
      <div className="flex justify-center mt-8">
        <Button
          label={language === 'lang_mal' ? 'കൂടുതൽ കാണുക' : 'View More'}
          className="lg:px-16 lg:min-h-12"
          onClick={() => navigate('/initiatives/training-calendar')}
        />
      </div>
    </div>
  )
}

export default TrainingCalendar
